/*
 * Decléor — app overlays (Judge.me, Klaviyo) → the theme's own scroll lock
 * ---------------------------------------------------------------------------
 * Three app overlays lock nothing of their own, so the store kept scrolling behind them and the
 * sticky add-to-cart bar (z-index 17999) floated over their backdrops:
 *
 *   .jdgm-review-widget-modal   the "Écrire un avis" form
 *   .jdgm-modal                 the review-detail lightbox, opened by clicking a review card
 *
 * Both are routed through the SAME scroll lock every drawer, popdown and lightbox in this theme
 * already uses, rather than by inventing a second one.
 *
 * WHY THAT ONE EVENT FIXES MORE THAN THE SCROLLING.
 * `theme:scroll:lock` makes theme.js set `data-scroll-locked` on <html>, and three things in the
 * stack are already keyed on that attribute:
 *     snippets/sticky-atc-style.liquid  hides the sticky add-to-cart bar
 *     assets/theme.css                  hides the newsletter popup, pads the header for the scrollbar
 *     assets/smooth-scroll.js           mirrors it onto lenis.stop() / lenis.start()
 * So the bar disappears, the popup cannot interrupt, and Lenis — which lerps the REAL window
 * scroll offset and would otherwise keep animating under the modal — stands down. Locking the
 * page by hand (overflow: hidden) would have got none of that and would have fought Lenis.
 *
 * The `detail` is the modal's scrollable panel, which is the contract theme.js implements:
 * `lock(e)` calls `scrollLock.disablePageScroll(e.detail)`. Without it the lock targets nothing
 * and a long form becomes unreachable on a phone. Same call shape as assets/product-routines.js.
 *
 * THE LOCK FOLLOWS HOW MANY ARE OPEN, NOT WHICH ONE CLOSED. The theme's lock is a single global
 * attribute, so with two modals sharing it the naive "unlock when this one closes" would release
 * the page while the other is still up. Every pass recounts the open modals and the lock follows
 * zero-vs-nonzero, so whichever closes last is the one that unlocks.
 *
 * HOW THE STATE IS READ — measured, not assumed. The two modals appear in different ways:
 *   - the FORM is server-rendered once as a direct child of <body> and toggled with an inline
 *     `display`. On OPEN, Judge.me removes the node and appends a fresh one (so an observer bound
 *     to the old node goes deaf — verified: a style observer attached before the click recorded
 *     nothing). On CLOSE the SAME node animates `opacity` inline for ~300ms, lands on
 *     `display: none`, and is then removed and re-appended again.
 *   - the LIGHTBOX does not exist at all until a card is clicked; it is created as a new <body>
 *     child (measured: absent before the click, present after).
 * One mechanism covers both: childList on <body> to notice a node arriving, leaving or being
 * swapped, plus a style/class observer on whatever nodes currently match. Every notification is
 * coalesced into one rAF — the form's close animation alone emits ~30 style mutations — and the
 * lock only fires when the effective count crosses zero, so a fade-out costs one computed-style
 * read per frame and nothing else.
 *
 * Reads `display` rather than opacity on purpose: a modal is still on screen while it fades, so
 * the page should still be locked.
 *
 * No-ops on any page without either modal.
 */
(function () {
  'use strict';

  /* Each entry is one of Judge.me's overlays and the element inside it that should stay
   * scrollable. The panels are tried in order and the first that exists is used; the modal root
   * is the fallback, so a markup change degrades to "page locked, panel not registered" rather
   * than to "lock targets nothing". */
  var MODALS = [
    {
      sel: '.jdgm-review-widget-modal',
      panels: ['.jdgm-write-review-modal__content'],
    },
    {
      sel: '.jdgm-modal',
      panels: ['.jdgm-modal__content', '.jdgm-modal__container'],
    },
    /* Klaviyo's signup popup. It advertises `data-kl-scroll-locking-modal` and does NOT lock the
     * page: measured, the wheel took the product page to scrollY 900 behind an open form, with no
     * `data-scroll-locked` on <html> and Lenis still running. It belongs in this list rather than in
     * a script of its own precisely because the theme's lock is ONE global attribute — a second
     * watcher would release the page while a Judge.me modal was still up, and vice versa. */
    {
      sel: '[data-kl-scroll-locking-modal]',
      panels: ['form.klaviyo-form', '[data-testid="modal-form-container"]'],
    },
  ];

  var locked = false; // our own view of the lock, so we never dispatch twice
  var watched = []; // the nodes the attribute observers are bound to
  var attrObservers = [];
  var frame = 0;
  var settle = 0; // the trailing re-read that lets a fade finish before it is judged

  function isOpen(el) {
    if (!(el && el.isConnected)) {
      return false;
    }
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') {
      return false;
    }
    /* FULLY TRANSPARENT COUNTS AS CLOSED, and this clause is the one that was missing.
     *
     * Klaviyo's popup does not change size, display or visibility when the visitor dismisses it.
     * Measured on a product page, before and after clicking its own close button, everything was
     * identical — `display: flex`, `visibility: visible`, height 844, still in the DOM, the form
     * still 244px tall — except ONE property on the modal root itself: opacity went 1 → 0. So the
     * two tests below both still said "open", `theme:scroll:unlock` never fired, and the page stayed
     * locked behind an invisible overlay. That is the bug this reads.
     *
     * EXACTLY zero, not a threshold: a Judge.me modal fades on opacity too, and the earlier note here
     * was right that a modal on its way OUT should keep the page locked until it has actually gone.
     * `=== 0` is only true at the very end of that fade, which is precisely when it has. The mirror
     * case — a modal read during the first frame of its fade IN, when opacity is still 0 — is what the
     * settle re-check in schedule() covers. */
    if (parseFloat(cs.opacity) === 0) {
      return false;
    }
    /* A COLLAPSED BOX COUNTS AS CLOSED, and that clause is what Klaviyo needs. Its popup is not
     * added and removed like the Judge.me pair: the element sits in the DOM from page load,
     * permanently `display: flex` and `visibility: visible`, and only its HEIGHT moves — measured 0
     * when closed, 900 when open. Reading display alone would have reported it open on every page
     * and locked the site for good. */
    return el.getBoundingClientRect().height > 1;
  }

  function panelOf(el, panels) {
    var i;
    var found;
    for (i = 0; i < panels.length; i += 1) {
      found = el.querySelector(panels[i]);
      if (found) {
        return found;
      }
    }
    return el;
  }

  /* Every node currently matching any of the selectors, in DOM order. */
  function present() {
    var out = [];
    var nodes;
    var i;
    var j;
    for (i = 0; i < MODALS.length; i += 1) {
      nodes = document.querySelectorAll(MODALS[i].sel);
      for (j = 0; j < nodes.length; j += 1) {
        out.push({ el: nodes[j], panels: MODALS[i].panels });
      }
    }
    return out;
  }

  /* Re-point the attribute observers when Judge.me creates, removes or swaps a node. Cheap to
   * call on every pass: it returns immediately while the set is unchanged, the common case. */
  function bind(nodes) {
    var same = nodes.length === watched.length;
    var i;
    var mo;
    if (same) {
      for (i = 0; i < nodes.length; i += 1) {
        if (nodes[i] !== watched[i]) {
          same = false;
          break;
        }
      }
    }
    if (same) {
      return;
    }
    for (i = 0; i < attrObservers.length; i += 1) {
      attrObservers[i].disconnect();
    }
    attrObservers = [];
    watched = nodes.slice();
    if (typeof MutationObserver !== 'function') {
      return;
    }
    for (i = 0; i < nodes.length; i += 1) {
      mo = new MutationObserver(schedule);
      mo.observe(nodes[i], {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      attrObservers.push(mo);
    }
  }

  /* A DISMISSED KLAVIYO POPUP STILL EATS EVERY CLICK. Its overlay is `position: fixed`, full screen,
   * z-index 90000, and on close only its opacity changes — `pointer-events` stays `auto`. Measured
   * after dismissing it: document.elementFromPoint at the middle of the product page returned an <img>
   * inside Klaviyo's own container, so nothing on the page could be clicked. Neutralising pointer
   * events on an overlay that is already invisible changes nothing a visitor can see, and it is put
   * back the moment the overlay is open again. */
  function setInert(el, inert) {
    if (el.style.pointerEvents !== (inert ? 'none' : '')) {
      el.style.pointerEvents = inert ? 'none' : '';
    }
  }

  function sync() {
    frame = 0;
    var found = present();
    var nodes = [];
    var open = [];
    var scrolls;
    var i;
    for (i = 0; i < found.length; i += 1) {
      nodes.push(found[i].el);
      if (isOpen(found[i].el)) {
        open.push(found[i]);
        setInert(found[i].el, false);
      } else {
        setInert(found[i].el, true);
      }
    }
    bind(nodes);

    var shouldLock = open.length > 0;
    if (shouldLock === locked) {
      return;
    }
    locked = shouldLock;

    if (shouldLock) {
      /* One element per open modal. scrollLock takes an array, which is how the theme registers
       * the cart drawer's two scrollable rows (theme.js:7164-7167). */
      scrolls = [];
      for (i = 0; i < open.length; i += 1) {
        scrolls.push(panelOf(open[i].el, open[i].panels));
      }
      document.dispatchEvent(
        new CustomEvent('theme:scroll:lock', {
          bubbles: true,
          detail: scrolls.length === 1 ? scrolls[0] : scrolls,
        })
      );
    } else {
      document.dispatchEvent(
        new CustomEvent('theme:scroll:unlock', { bubbles: true })
      );
    }
  }

  /* Read the DOM in a rAF, never in the mutation callback: on open the form's node is removed and
   * re-appended as two separate records, so a callback that ran per record would see a moment
   * with no modal at all and unlock a frame after locking. */
  function schedule() {
    if (frame) {
      return;
    }
    frame = window.requestAnimationFrame(sync);
    /* THE SETTLE PASS. Now that opacity is read, a single frame is no longer a safe moment to judge
     * an overlay by: both of these popups fade, so the frame a mutation lands on can catch one at
     * opacity 0 on its way IN (would be read as closed, page never locked) or mid-fade on its way OUT
     * (read as open, page stays locked until something else mutates — and nothing else does, which is
     * exactly how the original bug survived). One extra read once the animation has finished settles
     * it. 450ms comfortably clears the ~300ms fades measured on both. One timer at a time. */
    if (!settle) {
      settle = window.setTimeout(function () {
        settle = 0;
        sync();
      }, 450);
    }
  }

  if (typeof MutationObserver !== 'function') {
    return;
  }

  /* subtree: true, and Klaviyo is the reason. The Judge.me pair is appended and removed as DIRECT
   * children of <body>, so a shallow childList watch saw them. Klaviyo mounts a container of its own
   * at boot and then builds the popup THREE LEVELS DOWN inside it (measured: parent chain
   * body > div > div.needsclick > div.needsclick), so no record ever reached a shallow observer and
   * the lock never fired — the page scrolled to 1600 behind an open form.
   * Affordable because of the coalescing below rather than in spite of it: every record on the page,
   * however chatty Alpine and Lenis are, collapses into at most ONE sync per animation frame, and a
   * sync is a querySelectorAll plus one getComputedStyle per open modal. */
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  sync();
})();
